/**
 * Corrects for a device clock that disagrees with the server's.
 *
 * Every lecture countdown and join window (Start / Rejoin / Join buttons) is
 * computed in the browser against `new Date()`. A teacher whose laptop clock
 * is wrong - off by hours, or set to the wrong date - therefore saw "—" for
 * the remaining time and no Start button at all, while the same account on a
 * phone (network-synced clock) worked fine, and the backend itself was
 * already reporting the lecture as "ready".
 *
 * The API client feeds each response's `Date` header into
 * {@link recordServerDate}; {@link serverNow} then returns the device time
 * shifted by the measured offset. The header only has one-second resolution
 * and arrives after network latency, so drift within
 * {@link CLOCK_SKEW_TOLERANCE_MS} is ignored rather than making countdowns
 * jitter on a clock that is effectively correct.
 */
export const CLOCK_SKEW_TOLERANCE_MS = 5000;

let serverOffsetMs = 0;

/**
 * Records the server's time as reported by an HTTP `Date` header (or any
 * date string `Date` can parse). Unparseable or missing values are ignored.
 */
export function recordServerDate(
  serverDate: string | null | undefined,
  receivedAtMs: number = Date.now(),
): void {
  if (!serverDate) {
    return;
  }

  const serverMs = new Date(serverDate).getTime();

  if (Number.isNaN(serverMs)) {
    return;
  }

  const measuredOffsetMs = serverMs - receivedAtMs;

  if (Math.abs(measuredOffsetMs) <= CLOCK_SKEW_TOLERANCE_MS) {
    serverOffsetMs = 0;
    return;
  }

  if (Math.abs(measuredOffsetMs - serverOffsetMs) > CLOCK_SKEW_TOLERANCE_MS) {
    serverOffsetMs = measuredOffsetMs;
  }
}

/** Milliseconds to add to the device clock to get server time. */
export function getServerClockOffsetMs(): number {
  return serverOffsetMs;
}

/** The current time according to the server, as best known. */
export function serverNow(): Date {
  return new Date(Date.now() + serverOffsetMs);
}

/** Test helper: forget any measured offset. */
export function resetServerClock(): void {
  serverOffsetMs = 0;
}
